<template>
  <div class="login u-containar-md">
    <HeadingLv2 v-if="!hideHeading" text="ログイン" simple />
    <!-- 新デザイン -->
    <Alert
      simple
      :text="state.alertMessage"
      v-if="!useOutdatedStyle && state.alertMessage"
    />
    <SnsRegistButtons @click="toLine" />
    <!-- 旧デザイン -->
    <Alert
      :text="state.alertMessage"
      v-if="useOutdatedStyle && state.alertMessage"
    />
    <HrBlock or />

    <form action="/login" method="POST" class="login_form">
      <input type="hidden" name="_token" :value="csrf" />
      <input type="hidden" name="q" :value="state.q" />

      <FormInputField
        id="mail"
        name="email"
        :value="user.email"
        :error="validations.email.isError"
        :error-message="validations.email.errorMessage"
        label="メールアドレス"
        @change-message="changeMessage('email', $event)"
        placeholder="例：abd@example.com"
      />
      <FormInputField
        id="password"
        type="password"
        name="password"
        :password-switch="true"
        :value="user.password"
        :error="validations.password.isError"
        :error-message="validations.password.errorMessage"
        label="パスワード"
        patternLabel="半角英数字記号8～30文字"
        placeholder="パスワードを入力してください"
        @change-message="changeMessage('password', $event)"
      />

      <CheckboxButton
        class="autoLogin"
        id="auto_login"
        label="次回から自動的にログインする"
        inline
      />
      <div
        v-show="!state.isRecaptchaScore"
        class="g-recaptcha"
        data-callback="__getRecaptchaCheckboxToken"
        :data-sitekey="RECAPTCHA_SITEKEY_CHECKBOX"
      />
      <div class="login_btnArea">
        <Button
          label="ログイン"
          :handleClick="checkRecaptcha"
          primary
          border
          wide303
        />
        <TextLink
          link="/password"
          class="login_btnArea_link"
          text="パスワードを忘れた方はこちら"
        />
      </div>
    </form>

    <div v-if="!hideRegisterArea">
      <HrBlock />

      <div class="login_btnArea">
        <p class="login_btnArea_text">アカウントをお持ちでない方</p>
        <Button
          :href="registerPath"
          label="会員登録する"
          :handleClick="() => $router.push(registerPath)"
          secondary
          border
          wide303
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import axios from 'axios'
import {
  computed,
  defineComponent,
  reactive,
  ref,
  onMounted,
  onUnmounted,
  PropType
} from 'vue'
import { useStore } from 'vuex'
import { applyValidation } from '@/utils/validation'
import HeadingLv2 from '@/components/elements/HeadingLv2.vue'
import SnsRegistButtons from '@/components/block/SnsRegistButtons.vue'
import FormInputField from '@/components/block/FormInputField.vue'
import CheckboxButton from '@/components/elements/InputCheckboxButton.vue'
import Alert from '@/components/elements/Alert.vue'
import Button from '@/components/elements/Button.vue'
import HrBlock from '@/components/elements/HrBlock.vue'
import TextLink from '@/components/elements/TextLink.vue'
import { LoginInfo } from '@/types/LoginInfo'

declare let grecaptcha: any
declare let window: any

const RECAPTCHA_SITEKEY = process.env.VUE_APP_RECAPTCHA_SITEKEY || ''
const RECAPTCHA_SITEKEY_CHECKBOX =
  process.env.VUE_APP_RECAPTCHA_SITEKEY_CHECKBOX || ''
const RECAPTCHA_ACTION_NAME = process.env.VUE_APP_RECAPTCHA_ACTION_NAME || ''
const SOCIAL_PLUS_BASE_URL =
  'https://ca68f31f7a.auth.socialplus.jp/mynavi_resident/mynavi_resident_usr'

// NOTE: エラーの外部化はLogin.vueのデザインが統一されてから検討するほうがいいと思われます
export default defineComponent({
  name: 'Login',

  props: {
    defaultAlertMessage: {
      type: String as PropType<string>,
      default: ''
    },
    hideHeading: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    hideRegisterArea: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    redirect: {
      type: String as PropType<string>,
      default: ''
    },
    // TODO: 新デザインが浸透したら削除する
    useOutdatedStyle: {
      type: Boolean as PropType<boolean>,
      default: true
    }
  },

  components: {
    HeadingLv2,
    SnsRegistButtons,
    FormInputField,
    CheckboxButton,
    Alert,
    Button,
    HrBlock,
    TextLink
  },

  setup(props: any) {
    const store = useStore()

    const csrf = ref(
      document.querySelector('meta[name="csrf-token"]')!.getAttribute('content')
    )
    const user = reactive<LoginInfo>({
      email: '',
      password: ''
    })
    const state = reactive<{
      alertMessage: string
      q: string
      recaptchaScoreScript: HTMLScriptElement | null
      recaptchaCheckboxScript: HTMLScriptElement | null
      isRecaptchaScore: boolean
      recaptchaCheckToken: string
    }>({
      alertMessage: '',
      q: '',
      recaptchaScoreScript: null,
      recaptchaCheckboxScript: null,
      isRecaptchaScore: true,
      recaptchaCheckToken: ''
    })

    interface ValidationsObject {
      [key: string]: any
    }

    const validations: ValidationsObject = reactive({
      email: {
        rules: ['required', 'email'],
        isError: false,
        errorMessage: ''
      },
      password: {
        rules: ['required'],
        isError: false
      }
    })

    /**
     * reCAPTCHA scoreのロゴマークを削除する
     */
    const removeRecaptchaScoreLogo = () => {
      const logos = document.getElementsByClassName('grecaptcha-badge')
      for (const logo of logos) {
        const logoParent = logo.parentElement
        if (logoParent) {
          const base = logoParent.parentElement
          if (base) {
            base.removeChild(logoParent)
          } else {
            logoParent.removeChild(logo)
          }
        }
      }
    }

    /**
     * reCAPTCHAチェックボックスからの認証結果トークン取得コールバック
     */
    const getRecaptchaCheckboxToken = (recaptchaToken: string) => {
      state.recaptchaCheckToken = recaptchaToken
    }

    /**
     * reCAPTCHAチェックボックスscriptロード完了時のコールバック
     */
    const loadRecaptchaCheckbox = () => {
      grecaptcha.enterprise.render('recaptcha_checkbox', {
        sitekey: RECAPTCHA_SITEKEY_CHECKBOX,
        callback: getRecaptchaCheckboxToken
      })
    }

    const startRecaptchaCheckbox = () => {
      state.isRecaptchaScore = false
      removeRecaptchaScoreLogo()
      state.recaptchaCheckboxScript = document.createElement('script')
      state.recaptchaCheckboxScript.setAttribute(
        'src',
        `https://www.google.com/recaptcha/enterprise.js`
      )
      document.head.appendChild(state.recaptchaCheckboxScript)
      window.__getRecaptchaCheckboxToken = getRecaptchaCheckboxToken
    }

    const login = async (recaptchaType: string, recaptchaToken: string) => {
      store.commit('showLoading')
      try {
        const response = await axios.post('/api/checkLogin', {
          email: user.email,
          password: user.password,
          _token: csrf,
          'g-recaptcha-response': recaptchaToken,
          r_type: recaptchaType
        })
        const form: any = document.querySelector('.login_form')!
        form.submit()
      } catch (errors) {
        store.commit('closeLoading')
        if (errors.response.status === 429) {
          validations['password'].isError = true
          validations['password'].errorMessage = errors.response.data.errors[
            'email'
          ].join()
        } else if (errors.response.data) {
          const errorInfo = errors.response.data
          Object.keys(errorInfo).forEach((key) => {
            if (validations[key]) {
              validations[key].isError = true
              validations[key].errorMessage = errorInfo[key].join()
            }
          })
        }
        // reCAPTCHA checkboxによるチェックを実施する
        if (
          errors.response.status === 422 &&
          errors.response.data['reCaptchaCheckbox']
        ) {
          startRecaptchaCheckbox()
          alert(errors.response.data['message'])
        } else {
          // reCAPTCHA checkboxでトークンを送信している場合は、リセット実施
          if (state.isRecaptchaScore === false) {
            grecaptcha.enterprise.reset()
            state.recaptchaCheckToken = ''
          }
          alert('入力内容に誤りがあるので確認してください')
        }
      }
    }
    const checkRecaptcha = () => {
      if (state.isRecaptchaScore) {
        grecaptcha.enterprise.ready(function() {
          grecaptcha.enterprise
            .execute(RECAPTCHA_SITEKEY, { action: RECAPTCHA_ACTION_NAME })
            .then(function(token: string) {
              login('score', token)
            })
        })
      } else if (state.recaptchaCheckToken) {
        login('checkbox', state.recaptchaCheckToken)
      }
    }
    const changeMessage = (name: string, value: string) => {
      const result = applyValidation(validations[name].rules, value)

      validations[name].isError = result.isError
      validations[name].errorMessage = result.message
      user[name] = value
    }

    const toLine = () => {
      location.href = `${SOCIAL_PLUS_BASE_URL}/line/authenticate?callback=${process.env.VUE_APP_URL}/lineLogin?q=${state.q}`
    }

    onMounted(async () => {
      const params = new URLSearchParams(location.search)
      const paramThrow = params.get('throw')
      if (paramThrow === 'line') {
        state.alertMessage = 'ご利用のSNSアカウントでは会員登録されていません。'
      } else if (paramThrow === 'tokenError') {
        state.alertMessage =
          'LINEログインに失敗しました。再度ログインをお願いいたします。'
      } else if (props.defaultAlertMessage) {
        state.alertMessage = props.defaultAlertMessage
      }

      const q = params.get('q')
      if (q) {
        state.q = q
      }
      if (props.redirect) {
        state.q = props.redirect
      }

      state.recaptchaScoreScript = document.createElement('script')
      state.recaptchaScoreScript.setAttribute(
        'src',
        `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITEKEY}`
      )
      document.head.appendChild(state.recaptchaScoreScript)
    })

    onUnmounted(() => {
      document.head.removeChild(state.recaptchaScoreScript as Node)
      removeRecaptchaScoreLogo()
      if (state.recaptchaCheckboxScript) {
        document.head.removeChild(state.recaptchaCheckboxScript as Node)
        delete window.__getRecaptchaCheckboxToken
      }
    })

    const registerPath = computed(() => {
      const base = '/register/user_category'
      return state.q ? `${base}?q=${state.q}` : base
    })

    return {
      RECAPTCHA_SITEKEY_CHECKBOX,
      getRecaptchaCheckboxToken,
      loadRecaptchaCheckbox,
      checkRecaptcha,
      csrf,
      user,
      changeMessage,
      validations,
      toLine,
      state,
      registerPath
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.login {
  padding-bottom: 70px;

  &_btnArea {
    margin-top: 30px;
    padding-left: 20px;
    padding-right: 20px;
    text-align: center;

    &_link {
      margin-top: 25px;
      display: inline-block;
    }

    &_text {
      margin-bottom: 20px;
    }
  }
}
.g-recaptcha {
  margin: 20px 0 0 0;
}
.autoLogin {
  @include mixins.media(md) {
    margin-left: auto;
    width: calc(62.5% - 20px);
  }
}
</style>
